import type { ReactNode } from "react";

import type { DistrictHealthRow, DistrictRow } from "@busright/data-contract";
import { RAG } from "@busright/ui";

import { DataTable } from "@/components/DataTable";
import { Drillable } from "@/components/Drillable";
import { formatInt, formatScore } from "@/lib/format";
import { FLEET_SCOPE } from "@/lib/scope";

// =================================================================================================
// FhDistrictWorklist — the WHERE behind the composite: districts sorted by at-risk load, each row
// drilling into the device-level dataset for `health.composite` scoped to that district. Human
// district names come from /api/districts (accountName); synthetic seed/test rows (Szymon Prod
// District, Matt Demo District, the BusRight Team account, etc.) are filtered out so the worklist
// only ever shows real customer districts.
// =================================================================================================

// Case-insensitive name fragments that mark a row as seed/test data rather than a real customer.
const SYNTHETIC_NAME_PATTERNS: readonly RegExp[] = [
  /\bprod district\b/i,
  /\bdemo district\b/i,
  /busright team/i,
  /\btest district\b/i,
  /\bszymon\b/i,
  /\bmatt demo\b/i,
];

export const isSyntheticDistrictName = (name: string | null | undefined): boolean =>
  Boolean(name) && SYNTHETIC_NAME_PATTERNS.some((re) => re.test(name as string));

export interface FhDistrictWorklistProps {
  readonly rows: readonly DistrictHealthRow[];
  readonly districts: readonly DistrictRow[];
  readonly onOpenDistricts: () => void;
}

export const FhDistrictWorklist = ({
  rows,
  districts,
  onOpenDistricts,
}: FhDistrictWorklistProps): ReactNode => {
  const nameById = new Map(districts.map((d) => [d.districtId, d.accountName]));

  const visible = rows.filter((r) => !isSyntheticDistrictName(nameById.get(r.districtId)));

  return (
    <DataTable
      rows={visible}
      rowKey={(r) => r.districtId}
      emptyTitle="No districts"
      columns={[
        {
          key: "district",
          header: "District",
          // The district name IS the "open Districts" control (a real button) — kept independent of
          // the drill cell below so the two affordances never fight over the same click.
          render: (r) => (
            <button
              type="button"
              className="fh-district-name-link"
              onClick={onOpenDistricts}
              aria-label={`Open Districts — worklist for ${nameById.get(r.districtId) ?? r.districtId}`}
            >
              {nameById.get(r.districtId) ?? r.districtId}
            </button>
          ),
        },
        {
          key: "devices",
          header: "Devices",
          align: "right",
          render: (r) => <span className="mono">{formatInt(r.deviceCount)}</span>,
        },
        {
          key: "score",
          header: "Avg score",
          align: "right",
          render: (r) => <span className="mono">{formatScore(r.avgCompositeHealthScore)}</span>,
        },
        {
          key: "ok",
          header: "OK",
          align: "right",
          render: (r) => (
            <span className="mono" style={{ color: RAG.OK }}>
              {formatInt(r.riskCounts.ok)}
            </span>
          ),
        },
        {
          key: "warn",
          header: "WARN",
          align: "right",
          render: (r) => (
            <span className="mono" style={{ color: RAG.WARN }}>
              {formatInt(r.riskCounts.warn)}
            </span>
          ),
        },
        {
          key: "crit",
          header: "CRIT",
          align: "right",
          render: (r) => (
            <span className="mono" style={{ color: RAG.CRITICAL }}>
              {formatInt(r.riskCounts.critical)}
            </span>
          ),
        },
        {
          key: "drill",
          header: "Devices behind this",
          align: "right",
          render: (r) => (
            <Drillable
              metricKey="health.composite"
              scope={{
                level: "district",
                id: r.districtId,
                label: nameById.get(r.districtId) ?? r.districtId,
                parent: FLEET_SCOPE,
              }}
              label={`Drill into devices for ${nameById.get(r.districtId) ?? r.districtId}`}
            >
              <span className="mono">open</span>
            </Drillable>
          ),
        },
      ]}
    />
  );
};
